import React, { useState, useRef, useEffect } from 'react';
import { Mic, Loader2, AlertCircle } from 'lucide-react';
import styles from './DeviceSelector.module.css';

const MicSelector = ({
    selectedAudioDevice,
    onAudioDeviceChange,
    disabled: propDisabled,
    onMicToggle,
    isCoaching = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isMicEnabled, setIsMicEnabled] = useState(true);
    const dropdownRef = useRef(null);
    const mediaStreamRef = useRef(null);

    const loadDevices = async () => {
        if (!isMicEnabled) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            await navigator.mediaDevices.getUserMedia({ audio: true });
            const deviceList = await navigator.mediaDevices.enumerateDevices();

            const audioInputs = deviceList.filter(device => device.kind === 'audioinput');

            if (audioInputs.length === 0) {
                throw new Error('No microphone devices found');
            }

            setDevices(audioInputs);

            if (!selectedAudioDevice) {
                const defaultAudio = audioInputs.find(d => d.deviceId === 'default') || audioInputs[0];
                onAudioDeviceChange(defaultAudio.deviceId);
            }
        } catch (err) {
            setError(err.message || 'Failed to load microphone devices');
            console.error('Error loading microphone devices:', err);
        } finally {
            setLoading(false);
        }
    };

    const requestMicPermission = async () => {
        try {
            setLoading(true);
            setError(null);

            // First, request microphone permission
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            // If permission granted, enumerate devices
            const deviceList = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = deviceList.filter(device => device.kind === 'audioinput');

            if (audioInputs.length === 0) {
                throw new Error('No microphone devices found');
            }

            setDevices(audioInputs);

            // Select the first device or default device if no device was previously selected
            const defaultDevice = audioInputs.find(d => d.deviceId === 'default') || audioInputs[0];
            if (!selectedAudioDevice && defaultDevice) {
                onAudioDeviceChange(defaultDevice.deviceId);
            }

            // Stop the initial stream
            stream.getTracks().forEach(track => track.stop());

            return audioInputs;
        } catch (err) {
            console.error('Microphone permission error:', err);

            // Differentiate between different types of errors
            if (err.name === 'NotAllowedError') {
                setError('Microphone access denied. Please check your browser permissions.');
            } else if (err.name === 'NotFoundError') {
                setError('No microphone devices found. Please connect a microphone.');
            } else {
                setError(err.message || 'Failed to access microphone');
            }

            throw err;
        } finally {
            setLoading(false);
        }
    };

    const toggleMic = async () => {
        try {
            if (isMicEnabled) {
                // Disable microphone
                if (mediaStreamRef.current) {
                    const tracks = mediaStreamRef.current.getTracks();
                    tracks.forEach(track => track.stop());
                    mediaStreamRef.current = null;
                }
                setIsMicEnabled(false);
                setDevices([]); // Clear devices when mic is disabled
                onAudioDeviceChange(null);
                onMicToggle(false);
            } else {
                // Try to get microphone permission and devices
                const audioInputs = await requestMicPermission();

                // If devices found, select the first device
                const deviceToUse = selectedAudioDevice || audioInputs[0].deviceId;

                // Get a stream with the selected device
                const stream = await navigator.mediaDevices.getUserMedia({
                    audio: { deviceId: { exact: deviceToUse } }
                });

                mediaStreamRef.current = stream;
                setIsMicEnabled(true);

                if (!selectedAudioDevice) {
                    onAudioDeviceChange(deviceToUse);
                }

                onMicToggle(true);
            }
        } catch (err) {
            // Error handling is done in requestMicPermission
            onMicToggle(false);
            setIsMicEnabled(false);
            setDevices([]); // Clear devices on error
        }
    };
    useEffect(() => {
        loadDevices();
        navigator.mediaDevices.addEventListener('devicechange', loadDevices);
        return () => {
            navigator.mediaDevices.removeEventListener('devicechange', loadDevices);
            // Stop any active media stream on unmount
            if (mediaStreamRef.current) {
                const tracks = mediaStreamRef.current.getTracks();
                tracks.forEach(track => track.stop());
            }
        };
        // eslint-disable-next-line
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDeviceSelect = (deviceId) => {
        if (isMicEnabled) {
            onAudioDeviceChange(deviceId);
            setIsOpen(false);
        }
    };
    // eslint-disable-next-line
    const handleArrowClick = (e) => {
        e.stopPropagation();
        if (!propDisabled && !loading && !error) {
            setIsOpen(!isOpen);
        }
    };

    const handleDropdownToggle = (e) => {
        e.stopPropagation();
        if (!propDisabled && !loading && !error) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <div
            className={`${styles.deviceSelector}`}
            ref={dropdownRef}
        >
            <div className={styles.selectorContainer}>
                <div
                    className={`${styles.combinedButton} ${isMicEnabled ? styles.enabled : styles.disabled} ${propDisabled ? styles.disabled : ''}`}
                >
                    <div
                        className={styles.cameraSection}
                        onClick={toggleMic}
                        disabled={propDisabled}
                    >
                        {loading ? (
                            <Loader2 className={styles.spinningIcon} />
                        ) : error ? (
                            <AlertCircle className={styles.errorIcon} />
                        ) : (
                            <Mic className={styles.icon} />
                        )}
                    </div>

                    <div
                        className={styles.dropdownSection}
                        onClick={handleDropdownToggle}
                    >
                        <span className={`${styles.dropdownArrow} ${isOpen ? styles.open : ''}`}>
                            ▼
                        </span>
                    </div>
                </div>

                {isOpen && !loading && !error && (
                    <div className={`${styles.dropdownContent} ${styles.first} ${isCoaching ? styles.isCoaching : ''}`}>
                        <div className={styles.deviceList}>
                            {devices.map(device => (
                                <div
                                    key={device.deviceId}
                                    className={`${styles.deviceOption} ${selectedAudioDevice === device.deviceId ? styles.selected : ''}`}
                                    onClick={() => handleDeviceSelect(device.deviceId)}
                                >
                                    <div className={styles.deviceInfo}>
                                        <div className={styles.deviceLabel}>{device.label}</div>
                                        {device.deviceId === 'default' && (
                                            <div className={styles.defaultLabel}>System Default</div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button
                            className={styles.refreshButton}
                            onClick={(e) => {
                                e.stopPropagation();
                                loadDevices();
                            }}
                        >
                            Refresh devices
                        </button>
                    </div>
                )}

                {error && (
                    <div className={styles.errorTooltip}>
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MicSelector;